import React, { useEffect, useState } from "react";
import { DropZone } from "@beleef/ui";
import {
  FiChevronLeft,
  FiChevronRight,
  FiFile,
  FiFileText,
  FiFolder,
  FiImage,
  FiTrash2,
} from "react-icons/fi";
import SearchSelect from "components/ui/SearchSelect";
import { showToast } from "components/ui/toast";
import { useUploadFiles } from "apis/upload";
import {
  BRAND_ASSET_ACCEPT,
  BRAND_ASSET_CATEGORIES,
  BRAND_ASSET_LABELS,
  BRAND_ASSET_STATUS_OPTIONS,
  BRAND_ASSET_TAGS,
  buildBrandAssetState,
  getUploadedUrl,
  toBrandAssetArray,
} from "utils/network";

export const fileIcon = (name = "") => {
  const extension = name.split(".").pop()?.toLowerCase();

  if (extension === "pdf") return FiFileText;
  if (["png", "jpg", "jpeg", "webp", "gif"].includes(extension)) return FiImage;
  return FiFile;
};

export const AssetFolderTile = ({ label, count, status, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    className="flex items-center gap-3 rounded-lg border border-mediumgray bg-white px-3.5 py-3 text-left transition-colors hover:border-darkgray hover:bg-lightgray"
  >
    <FiFolder className="h-5 w-5 flex-shrink-0 text-darkgray" />
    <div className="min-w-0 flex-1">
      <div className="truncate text-[13px] font-medium text-black">{label}</div>
      <div className="text-[11px] text-darkgray">
        {count === 0 ? "Empty" : `${count} file${count === 1 ? "" : "s"}`}
      </div>
    </div>
    {count > 0 && status ? (
      <span
        className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${status.className}`}
      >
        {status.label}
      </span>
    ) : null}
  </button>
);

export const AssetFileRow = ({
  name,
  href,
  tag,
  tags,
  onTagChange,
  onRemove,
  disabled,
}) => {
  // A tag set by the app may not be one this category offers — keep it rather
  // than silently rewriting it to the first option on save.
  const options = tag && !tags.includes(tag) ? [tag, ...tags] : tags;
  const tagOptions = options.map((option) => ({
    value: option,
    label: option,
  }));
  const Icon = fileIcon(name);

  return (
    <div className="flex flex-col gap-2.5 border-t border-mediumgray px-3.5 py-3 first:border-t-0 sm:flex-row sm:items-center sm:gap-3">
      <div className="flex min-w-0 flex-1 items-center gap-2.5">
        <Icon className="h-4 w-4 flex-shrink-0 text-darkgray" />
        <div className="min-w-0">
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="block truncate text-[13px] text-black hover:underline"
            >
              {name}
            </a>
          ) : (
            <>
              <div className="truncate text-[13px] text-black">{name}</div>
              <div className="text-[11px] text-darkgray">Uploads on save</div>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 sm:flex-shrink-0">
        <div className="min-w-0 flex-1 sm:w-44 sm:flex-none">
          <SearchSelect
            options={tagOptions}
            value={tagOptions.find((option) => option.value === tag) || null}
            onChange={(option) => onTagChange(option?.value || "")}
            placeholder="No tag"
            searchable={false}
            isClearable
            disabled={disabled}
          />
        </div>
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          aria-label={`Remove ${name}`}
          title="Remove"
          className="flex-shrink-0 rounded p-1.5 text-red-500 hover:bg-red-50 disabled:opacity-50"
        >
          <FiTrash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// Brand assets as folders, one per category. Uploads land in S3 raw and save
// straight away — there is no surrounding form to submit.
const BrandAssetsBrowser = ({ assets, folderFor, onSave, saving }) => {
  const [state, setState] = useState(() => buildBrandAssetState(assets));
  const [openCategory, setOpenCategory] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { uploadSingle } = useUploadFiles();

  useEffect(() => {
    setState(buildBrandAssetState(assets));
  }, [assets]);

  const commit = (next) => {
    setState(next);
    onSave(toBrandAssetArray(next));
  };

  const updateCategory = (category, changes) =>
    commit({
      ...state,
      [category]: {
        status: "pending",
        submittedAt: "",
        files: [],
        ...state[category],
        ...changes,
      },
    });

  const handleFilesSelected = (category) => async (files) => {
    const picked = Array.from(files || []).filter(Boolean);
    if (!picked.length) return;

    setUploading(true);
    try {
      const uploaded = await Promise.all(
        picked.map(async (file) => {
          const result = await uploadSingle({
            file,
            folder: folderFor(category),
            raw: true,
          });
          const fileUrl = getUploadedUrl(result);

          if (!fileUrl) throw new Error(`${file.name} upload failed`);
          return { fileUrl, originalName: file.name, tag: "" };
        }),
      );

      updateCategory(category, {
        files: [...(state[category]?.files || []), ...uploaded],
      });
    } catch (error) {
      showToast("error", error?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const disabled = saving || uploading;

  if (!openCategory) {
    return (
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
        {BRAND_ASSET_CATEGORIES.map((category) => (
          <AssetFolderTile
            key={category}
            label={BRAND_ASSET_LABELS[category]}
            count={state[category]?.files?.length || 0}
            onOpen={() => setOpenCategory(category)}
          />
        ))}
      </div>
    );
  }

  const files = state[openCategory]?.files || [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => setOpenCategory(null)}
          className="flex items-center gap-1 rounded px-1.5 py-1 text-[13px] text-darkgray hover:bg-lightgray hover:text-black"
        >
          <FiChevronLeft className="h-4 w-4" />
          Brand assets
        </button>
        <FiChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-darkgray" />
        <span className="truncate text-[13px] font-medium text-black">
          {BRAND_ASSET_LABELS[openCategory]}
        </span>
      </div>

      <div className="sm:max-w-xs">
        <SearchSelect
          options={BRAND_ASSET_STATUS_OPTIONS}
          value={
            BRAND_ASSET_STATUS_OPTIONS.find(
              (option) =>
                option.value === (state[openCategory]?.status || "pending"),
            ) || null
          }
          onChange={(option) =>
            updateCategory(openCategory, {
              status: option?.value || "pending",
            })
          }
          searchable={false}
          disabled={disabled}
        />
      </div>

      {files.length > 0 ? (
        <div className="rounded-md border border-mediumgray">
          {files.map((file, index) => (
            <AssetFileRow
              key={file.fileUrl}
              name={file.originalName || file.fileUrl.split("/").pop()}
              href={file.fileUrl}
              tag={file.tag}
              tags={BRAND_ASSET_TAGS[openCategory] || []}
              onTagChange={(tag) =>
                updateCategory(openCategory, {
                  files: files.map((entry, position) =>
                    position === index ? { ...entry, tag } : entry,
                  ),
                })
              }
              onRemove={() =>
                updateCategory(openCategory, {
                  files: files.filter((_, position) => position !== index),
                })
              }
              disabled={disabled}
            />
          ))}
        </div>
      ) : null}

      <DropZone
        onFilesSelected={handleFilesSelected(openCategory)}
        accept={BRAND_ASSET_ACCEPT}
        multiple
        disabled={disabled}
        title={
          uploading
            ? "Uploading…"
            : `Upload ${BRAND_ASSET_LABELS[openCategory].toLowerCase()} artwork`
        }
        subtitle="PDF, AI, EPS, PNG or JPG — up to 50MB each"
      />
    </div>
  );
};

export default BrandAssetsBrowser;
